/**
 * @fileoverview Health API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Health module.
 * Single source of truth for all health check API definitions.
 *
 * NOTE: Re-uses HealthStatus enum from api.types.ts to avoid duplication.
 */

import { z } from 'zod';
import { createContract, extractRoutes } from './contract.utils';

// Re-export HealthStatus from api.types.ts (already defined there)
export { HealthStatus } from '../api.types';

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Memory Usage Schema
 */
export const MemoryUsageSchema = z.object({
	rss: z.number(),
	heapTotal: z.number(),
	heapUsed: z.number(),
	external: z.number(),
	arrayBuffers: z.number(),
});
export type MemoryUsage = z.infer<typeof MemoryUsageSchema>;

/**
 * Simple Health Check Response Schema
 * For basic health endpoint (not the full system health check)
 */
export const SimpleHealthCheckResponseSchema = z.object({
	status: z.enum(['healthy', 'degraded', 'unhealthy', 'maintenance']),
	timestamp: z.string(), // ISO 8601 datetime
	uptime: z.number(), // Seconds
	memory: MemoryUsageSchema,
	maintenanceMode: z.boolean(),
});
export type SimpleHealthCheckResponse = z.infer<typeof SimpleHealthCheckResponseSchema>;

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Health API Contract
 *
 * Defines the complete API surface for the Health module.
 * Both frontend and backend use this as the single source of truth.
 */
export const HEALTH_API_CONTRACT = createContract('/health', {
	/**
	 * GET /health
	 * Simple health check endpoint
	 */
	check: {
		method: 'GET' as const,
		path: '',
		response: SimpleHealthCheckResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const HEALTH_ROUTES = extractRoutes(HEALTH_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type HealthApiContract = typeof HEALTH_API_CONTRACT;
